import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Stack,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    CircularProgress,
    Button,
    TablePagination,
    Alert,
    Link,
    Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    fetchReviewQueue,
    ReviewQueueQuery,
} from '../api/workerCertificationsApi';
import {
    WorkerCertification,
    CertReviewStatus,
    CertCategory,
} from '../models/worker';
import CertReviewDialog from '../components/CertReview/CertReviewDialog';
import socketService from '../services/socketService';

const STATUS_COLOR: Record<CertReviewStatus, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
    [CertReviewStatus.PENDING_EXTRACTION]: 'info',
    [CertReviewStatus.PENDING_REVIEW]: 'warning',
    [CertReviewStatus.VERIFIED]: 'success',
    [CertReviewStatus.REJECTED]: 'error',
    [CertReviewStatus.MANUAL_UNVERIFIED]: 'default',
    [CertReviewStatus.REUPLOAD_REQUIRED]: 'warning',
};

const minConfidence = (cert: WorkerCertification): number | null => {
    if (!cert.aiConfidence) return null;
    const vals = Object.values(cert.aiConfidence).filter(v => typeof v === 'number');
    if (!vals.length) return null;
    return Math.min(...vals);
};

const CertReviewQueuePage: React.FC = () => {
    const [items, setItems] = useState<WorkerCertification[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);
    const [statusFilter, setStatusFilter] = useState<CertReviewStatus | ''>(CertReviewStatus.PENDING_REVIEW);
    const [categoryFilter, setCategoryFilter] = useState<CertCategory | ''>('');
    const [selected, setSelected] = useState<WorkerCertification | null>(null);

    const query = useMemo<ReviewQueueQuery>(() => ({
        status: statusFilter ? [statusFilter] : undefined,
        certCategory: categoryFilter || undefined,
        page: page + 1,
        limit,
    }), [statusFilter, categoryFilter, page, limit]);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchReviewQueue(query);
            setItems(res.items);
            setTotal(res.total);
        } catch (e: any) {
            // Backend validation errors can be string, array of strings, or a
            // nested object. Normalise to a single string for the Alert.
            const raw = e?.response?.data?.message ?? e?.message ?? 'Failed to load review queue.';
            const msg = Array.isArray(raw)
                ? raw.join('; ')
                : typeof raw === 'string'
                    ? raw
                    : JSON.stringify(raw);
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Live updates: a new cert was extracted, or a row's status changed.
    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket) return;
        const onComplete = () => {
            refresh();
        };
        socket.on('certExtractionComplete', onComplete);
        return () => {
            socket.off('certExtractionComplete', onComplete);
        };
    }, [refresh]);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Certificate review queue</Typography>
                <Box sx={{ flex: 1 }} />
                <Button onClick={refresh} disabled={loading}>Refresh</Button>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={e => { setStatusFilter(e.target.value as CertReviewStatus | ''); setPage(0); }}
                        >
                            <MenuItem value="">(all)</MenuItem>
                            {Object.values(CertReviewStatus).map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={categoryFilter}
                            label="Category"
                            onChange={e => { setCategoryFilter(e.target.value as CertCategory | ''); setPage(0); }}
                        >
                            <MenuItem value="">(any)</MenuItem>
                            {Object.values(CertCategory).map(c => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper>
                {loading ? (
                    <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Worker</TableCell>
                                    <TableCell>Cert</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Issuer</TableCell>
                                    <TableCell>Issue → Expiry</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <span>Min conf.</span>
                                            <Tooltip
                                                title="Lowest AI confidence across the extracted fields (0–1). Lower values mean the AI was less certain — worth a closer look."
                                                arrow
                                                placement="top"
                                            >
                                                <InfoOutlinedIcon
                                                    fontSize="inherit"
                                                    sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                                                />
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>Flags</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map(cert => {
                                    const c = minConfidence(cert);
                                    return (
                                        <TableRow
                                            key={cert.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setSelected(cert)}
                                        >
                                            <TableCell onClick={e => e.stopPropagation()}>
                                                {cert.workerStringId ? (
                                                    <Link
                                                        component={RouterLink}
                                                        to={`/workers/${cert.workerStringId}`}
                                                        underline="hover"
                                                    >
                                                        {cert.workerPhoneNumber
                                                            || [cert.workerFirstName, cert.workerLastName].filter(Boolean).join(' ')
                                                            || cert.workerStringId}
                                                    </Link>
                                                ) : (
                                                    cert.workerId
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <strong>{cert.proposedCertName || '(unidentified)'}</strong>
                                            </TableCell>
                                            <TableCell>{cert.certCategory ?? '—'}</TableCell>
                                            <TableCell>{cert.issuer ?? '—'}</TableCell>
                                            <TableCell>
                                                expires {cert.expiryDate ?? '?'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={cert.status}
                                                    color={STATUS_COLOR[cert.status]}
                                                />
                                            </TableCell>
                                            <TableCell>{cert.sourceChannel}</TableCell>
                                            <TableCell>{c !== null ? c.toFixed(2) : '—'}</TableCell>
                                            <TableCell>
                                                {(cert.validationFlags?.length ?? 0) > 0
                                                    ? `${cert.validationFlags!.length} flag${cert.validationFlags!.length > 1 ? 's' : ''}`
                                                    : '—'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            No certs in this view.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={limit}
                            onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </>
                )}
            </Paper>

            <CertReviewDialog
                open={!!selected}
                cert={selected}
                onClose={() => setSelected(null)}
                onChanged={updated => {
                    setItems(prev => prev.map(c => (c.id === updated.id ? updated : c)));
                    setSelected(updated);
                    // Refresh table to drop confirmed/rejected from the queue view.
                    setTimeout(refresh, 200);
                }}
            />
        </Container>
    );
};

export default CertReviewQueuePage;
